import {
  CircularProgress,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
} from '@mui/material'
import {
  ArrowDropUp,
  AttachMoney,
  InfoOutlined,
  InsertDriveFileOutlined,
  Article,
  RecordVoiceOver,
} from '@mui/icons-material'
import { connect, ErrorMessage, FieldArray, FormikContextType, getIn } from 'formik'
import { inject } from 'mobx-react'
import moment from 'moment'
import React from 'react'
import AddressField from '../common/AddressField/AddressField'
import DatePickerField from '../common/DatePickerField/DatePickerField'
import FullWidthField from '../common/FullWidthField'
import PhoneNumberMask from '../common/PhoneNumberMask'
import SelectField from '../common/SelectField/SelectField'
import TitleBar from '../common/TitleBar/TitleBar'
import TypeAheadField from '../common/TypeAheadField/TypeAheadField'
import { ICMS1500, ILocation, ISchedule } from '../Definitions'
import {
  AddressDto,
  EpisodeOfCareDto,
  IEpisodeOfCareDto,
  IProviderDto,
  IProviderTypeDto,
} from '../generated_client'
import { IStores } from '../Stores'
import States, { IState } from '../utils/States'
import Box24 from './Box24'
import { createIClaimFormValues, Diagnosis, IClaimFormValues } from './ClaimFormValues'
import { ILineItemFormValues } from './LineItemFormValues'
import './ClaimForm.css'
import AppealNameConstants from 'src/constants/AppealNameConstants'
import ClaimDocumentButton from './ClaimDocumentButton'
import GeneratePDFDocumentButton from './GeneratePDFDocumentButton'
import QuoteTypeNameConstants from 'src/constants/QuoteTypeNameConstants'
import ReferralContactModal from 'src/patients/EpisodesOfCare/ReferralContactModal'

interface IFullFormProps {
  episodeOfCare?: IEpisodeOfCareDto
  loadClaim?: (claimId: string) => Promise<void>
  selectedCMS1500?: ICMS1500
  selectedSchedule?: ISchedule
  selectedLocation?: ILocation
  setSelectedCMS1500?: (cms1500: ICMS1500 | undefined) => void
  errorState?: boolean
  dueDateBefore?: boolean
  showContractModal?: (provider: IProviderDto | undefined) => void
  dueDate?: string | undefined
  handleDueDateChange?: (dueDateVal: string) => void
  receivedDate?: string | undefined
  surgeryDate?: Date | undefined
  handleDateChange?: (event: any) => void
  openContractModal?: (index: number) => void
  showContractModalButton: boolean
  showPDFButton: boolean
  hasNotSubmitted: boolean
  isReferralContactModalOpen?: boolean
  openReferralContactModal?: () => void
}

interface IFormikProps {
  formik: FormikContextType<IClaimFormValues>
}

class FullForm extends React.Component<IFullFormProps & IFormikProps> {
  constructor(props: IFullFormProps & IFormikProps) {
    super(props)
  }

  public state = {
    isLoading: false,
  }

  public initialValues = createIClaimFormValues(
    this.props.selectedCMS1500,
    new EpisodeOfCareDto(this.props.episodeOfCare)
  )

  public getLinkPath() {
    return (
      '/patients/patient/' +
      this.props.episodeOfCare?.referral?.patientId +
      '/referral/' +
      this.props.episodeOfCare?.referralId +
      '/episodeOfCare/' +
      this.props.episodeOfCare?.id
    )
  }

  public render() {
    const { formik, showContractModal } = this.props
    const isFullyReconciled =
      (this.props.selectedCMS1500?.percentageOfLineItemsReconciled ?? 0) >= 100
    const disableDollarAmounts =
      isFullyReconciled ||
      [
        'Denied',
        'Paid',
        'Paid-Holding',
        'Imported Paid',
        'Imported Denied',
        'Deleted',
        'Imported',
        'Refunded',
      ].includes(this.props.selectedCMS1500?.billStatus ?? '')
    const reconciliationLabel = this.props.selectedCMS1500?.inGlobal ? (
      <span style={{ fontSize: '16px' }}>
        {' '}
        (Claim Reconciliation:{' '}
        {this.props.selectedCMS1500.percentageOfLineItemsReconciled ?? 0}%)
      </span>
    ) : undefined

    if (this.props.selectedSchedule && this.props.selectedSchedule!.appointmentDate) {
      formik.values.dateOfService = this.props.selectedSchedule!.appointmentDate!
      this.initialValues.dateOfService = formik.values.dateOfService

      if (this.initialValues.q14DateOfIllness == undefined) {
        formik.values.q14DateOfIllness =
          this.props.episodeOfCare?.referralDOI ??
          this.props.selectedSchedule!.appointmentDate!
        this.initialValues.q14DateOfIllness = formik.values.q14DateOfIllness
      }
    } else if (this.initialValues.q14DateOfIllness == undefined) {
      formik.values.q14DateOfIllness = this.props.episodeOfCare?.referralDOI ?? new Date()
      this.initialValues.q14DateOfIllness = formik.values.q14DateOfIllness
    }

    const hasValue = (field: string, values: IClaimFormValues) => {
      const value = getIn(values, field)
      return !!value
    }

    const getStateName = (state: IState) => {
      return state.name
    }

    return (
      <Grid style={{ padding: '8px' }}>
        {this.state.isLoading && (
          <div
            style={{
              position: 'fixed',
              display: 'flex',
              width: '100%',
              height: '100%',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              cursor: 'pointer',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: '2',
            }}
          >
            <CircularProgress size={100} />
          </div>
        )}
        {this.props.isReferralContactModalOpen && (
          <ReferralContactModal
            selectedReferralId={this.props.episodeOfCare?.referralId!}
          />
        )}
        <Grid item>
          <Grid container style={{ padding: 8 }}>
            <Accordion
              defaultExpanded
              sx={{ marginBottom: '8px', border: '1px solid', borderColor: 'divider' }}
            >
              <AccordionSummary expandIcon={<ArrowDropUp />}>
                <TitleBar
                  title={
                    <>
                      {'General - ' +
                        (this.props.selectedCMS1500?.billStatus == 'New'
                          ? 'RECEIVED'
                          : this.props.selectedCMS1500?.billStatus
                            ? this.props.selectedCMS1500?.billStatus.toUpperCase()
                            : '[UNKNOWN]')}
                      {reconciliationLabel}
                    </>
                  }
                  titleButton={
                    this.props.selectedCMS1500?.billStatus === 'Denied' &&
                    this.props.selectedCMS1500.denialLetterDocumentUrl ? (
                      <Tooltip title="Show Latest Denial Documentation">
                        <Article
                          fontSize="small"
                          color="error"
                          aria-label="View latest denial documentation"
                          style={{ cursor: 'pointer' }}
                          onClick={(event) => {
                            event.stopPropagation()
                            window.open(
                              this.props.selectedCMS1500!.denialLetterDocumentUrl!
                            )
                          }}
                        />
                      </Tooltip>
                    ) : undefined
                  }
                  icon={AttachMoney}
                  titleComponent={
                    <div style={{ paddingTop: '4px', paddingLeft: '8px' }}>
                      {this.props.showPDFButton && (
                        <GeneratePDFDocumentButton
                          claimId={formik.values.id}
                          disabled={formik.values.id == '' || this.props.hasNotSubmitted}
                          setIsLoading={(loading: boolean) =>
                            this.setState({ isLoading: loading })
                          }
                        />
                      )}
                      <ClaimDocumentButton
                        docId={this.props.selectedCMS1500?.stagedDocumentId ?? ''}
                        setIsLoading={(loading: boolean) =>
                          this.setState({ isLoading: loading })
                        }
                      />
                    </div>
                  }
                  isTitleComponentPushedRight={true}
                />
              </AccordionSummary>
              <AccordionDetails>
                <Grid container item spacing={1}>
                  <Grid item container xs={12} spacing={2}>
                    <Grid item>
                      <strong>Patient Name:</strong>&nbsp;
                      {this.props.episodeOfCare
                        ? this.props.episodeOfCare.referral!.patient!.firstName +
                          ' ' +
                          this.props.episodeOfCare.referral!.patient!.lastName
                        : 'undefined'}
                    </Grid>
                    <Grid
                      item
                      container
                      spacing={1}
                      xs={1}
                      style={{ minWidth: 'fit-content' }}
                    >
                      <Grid item>
                        <strong>EOC/Legacy:</strong>
                      </Grid>
                      <Grid item>
                        <Button
                          variant="text"
                          color="primary"
                          sx={{ userSelect: 'text' }}
                          style={{
                            textTransform: 'none',
                            padding: 0,
                            minWidth: 0,
                            background: 'none',
                            border: 'none',
                            boxShadow: 'none',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            fontWeight: 'normal',
                            fontSize: 'inherit',
                            lineHeight: 'inherit',
                          }}
                          onMouseOver={(e) => {
                            ;(e.currentTarget as HTMLElement).style.textDecoration =
                              'underline'
                          }}
                          onMouseOut={(e) => {
                            ;(e.currentTarget as HTMLElement).style.textDecoration =
                              'underline'
                          }}
                          onClick={() => {
                            sessionStorage.setItem('patientTab', '0')
                            sessionStorage.setItem('eocTab', '1')
                            window.open(this.getLinkPath())
                          }}
                        >
                          {this.props.episodeOfCare
                            ? this.props.episodeOfCare.episodeOfCareNumberOrLegacyId
                            : 'undefined'}
                        </Button>
                      </Grid>
                    </Grid>
                    <Grid item>
                      <Tooltip title="Show Referral Contact">
                        <RecordVoiceOver
                          fontSize="small"
                          color={'primary'}
                          style={{
                            cursor: 'pointer',
                          }}
                          onClick={this.props.openReferralContactModal}
                        />
                      </Tooltip>
                    </Grid>
                    <Grid item>
                      <strong>Group #:</strong>&nbsp;
                      {this.props.selectedCMS1500
                        ? this.props.selectedCMS1500.groupNumber
                          ? this.props.selectedCMS1500.groupNumber
                          : 'TBD'
                        : 'TBD'}
                    </Grid>
                    <Grid item>
                      <strong>Body Part:</strong>&nbsp;
                      {this.props.selectedSchedule?.bodyParts != undefined
                        ? this.props.selectedSchedule!.bodyParts
                        : this.props.episodeOfCare
                          ? this.props.episodeOfCare!.bodyPartText
                          : ''}
                    </Grid>
                    <Grid item>
                      {this.props.selectedCMS1500?.appealType != undefined ? (
                        <Typography noWrap variant="body2" color="error">
                          <strong>Appeal Type:</strong>&nbsp;
                          {AppealNameConstants[this.props.selectedCMS1500?.appealType]}
                        </Typography>
                      ) : (
                        <></>
                      )}
                    </Grid>
                  </Grid>
                  <Grid item container xs={12} spacing={2}>
                    <Grid item>
                      <strong>ToC Date:</strong>&nbsp;
                      {this.props.episodeOfCare != undefined &&
                      this.props.episodeOfCare.effectiveDate != undefined
                        ? moment(this.props.episodeOfCare.effectiveDate).format('L')
                        : 'N/A'}
                    </Grid>
                    <Grid item>
                      <strong>Bundle Type:</strong>&nbsp;
                      {this.props.episodeOfCare != undefined &&
                      this.props.episodeOfCare.quote != undefined &&
                      this.props.episodeOfCare.quote.type != undefined
                        ? QuoteTypeNameConstants[this.props.episodeOfCare.quote.type]
                        : 'N/A'}
                    </Grid>
                    <Grid item>
                      <strong>Surgery Date:</strong>&nbsp;
                      {this.props.surgeryDate != undefined
                        ? moment(this.props.surgeryDate).format('L')
                        : 'N/A'}
                    </Grid>
                    <Grid item>
                      <strong># of Days:</strong>&nbsp;
                      {this.props.episodeOfCare != undefined &&
                      this.props.episodeOfCare.quote != undefined &&
                      this.props.episodeOfCare.quote.bundleDurationDays != undefined
                        ? this.props.episodeOfCare.quote.bundleDurationDays
                        : 'N/A'}
                    </Grid>
                    <Grid item>
                      <strong>End Date:</strong>&nbsp;
                      {this.props.episodeOfCare != undefined &&
                      this.props.episodeOfCare.quote != undefined &&
                      this.props.episodeOfCare.quote.endDate != undefined &&
                      this.props.selectedCMS1500?.inGlobal
                        ? moment(this.props.episodeOfCare.quote.endDate).format('L')
                        : 'N/A'}
                    </Grid>
                  </Grid>
                  <Grid
                    container
                    item
                    spacing={2}
                    xs={12}
                    style={{ paddingBottom: '34px' }}
                  >
                    <Grid item>
                      <strong>Date of Service:</strong>&nbsp;
                      {moment(formik.values.dateOfService).format('L')}
                    </Grid>
                    <Grid item>
                      <strong>Provider Group:</strong>&nbsp;
                      <span style={{ paddingRight: '4px' }}>
                        {this.getLocationProvider(formik.values)}
                      </span>
                      <Tooltip title="View Provider Contract Summary">
                        <InsertDriveFileOutlined
                          style={{
                            cursor: 'pointer',
                            fontSize: '16px',
                          }}
                          onClick={() => {
                            showContractModal!(formik.values.providerLocation?.provider)
                          }}
                        />
                      </Tooltip>
                    </Grid>
                    <Grid item>
                      <strong>Provider Location:</strong>&nbsp;
                      {`${formik.values.providerLocation?.name} ${formik.values.providerLocation?.physicalAddress?.addressDisplay}`}
                      {formik.values.providerLocation &&
                        formik.values.q33BillingProviderInfo && (
                          <Tooltip
                            componentsProps={{
                              tooltip: { sx: { fontSize: '0.85rem', p: 1.5 } },
                            }}
                            title={
                              <span style={{ whiteSpace: 'pre-line' }}>
                                <strong>Billing Address:</strong>
                                {' \n'}
                                {formik.values.q33BillingProviderInfo}
                              </span>
                            }
                          >
                            <InfoOutlined
                              style={{
                                fontSize: '20px',
                                marginLeft: '4px',
                                cursor: 'pointer',
                                verticalAlign: 'middle',
                              }}
                            />
                          </Tooltip>
                        )}
                    </Grid>
                  </Grid>
                  <fieldset
                    disabled={isFullyReconciled}
                    style={{
                      border: 0,
                      margin: 0,
                      minWidth: 0,
                      padding: 0,
                      width: '100%',
                    }}
                  >
                    <Grid
                      container
                      spacing={2}
                      style={{ paddingBottom: 12 }}
                      alignItems="flex-end"
                    >
                      <Grid item xs={3}>
                        <SelectField
                          label="Provider Type"
                          name="providerTypeId"
                          inputId="providerTypeId"
                          getName={this.getProviderTypeName}
                          getValue={this.getProviderTypeValue}
                          items={this.getProviderTypes(formik.values)}
                          fullWidth
                          disabled={
                            isFullyReconciled ||
                            this.isProviderTypeDisabled(formik.values)
                          }
                          shrinkLabel={!!formik.values.providerTypeId}
                          required
                          outlined={false}
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <TextField
                          disabled={isFullyReconciled}
                          inputProps={{
                            id: 'incomingOrOutgoingDate',
                          }}
                          InputLabelProps={{ shrink: true }}
                          onChange={this.props.handleDateChange}
                          type="date"
                          name="incomingOrOutgoingDate"
                          variant="standard"
                          value={this.props.receivedDate}
                          label="Date Received"
                          fullWidth
                          required
                          error={this.props.receivedDate == ''}
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <TextField
                          disabled={isFullyReconciled}
                          inputProps={{
                            id: 'dueDate',
                          }}
                          InputLabelProps={{ shrink: true }}
                          type="date"
                          name="dueDate"
                          onChange={this.handleDueDateChange}
                          variant="standard"
                          value={this.props.dueDate}
                          label="Date Due"
                          fullWidth
                          required
                          error={this.props.dueDate == ''}
                        />
                        {this.props.dueDateBefore && (
                          <div className="error">
                            Due date must occur on or after Date Received.
                          </div>
                        )}
                      </Grid>
                      <Grid item xs={2}>
                        <FullWidthField
                          variant="standard"
                          name="q28TotalCharge"
                          label="Total Charge"
                          type="number"
                          required
                          disabled={disableDollarAmounts}
                          InputLabelProps={{
                            shrink: hasValue('q28TotalCharge', formik.values),
                          }}
                        />
                        {this.props.errorState && (
                          <div className="error">
                            Sum of claim line charges must match Total Charges entered.
                          </div>
                        )}
                      </Grid>
                      {this.props.selectedCMS1500?.appealType != undefined && (
                        <Grid item xs={12}>
                          <div className="red-outline">
                            <FullWidthField
                              disabled={isFullyReconciled}
                              name="appealReason"
                              label="Appeal Reason"
                              type="text"
                              required
                              fullWidth
                              multiline
                              rows="4"
                              style={{ marginBottom: 4 }}
                              variant="outlined"
                            />
                          </div>
                        </Grid>
                      )}
                    </Grid>
                  </fieldset>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Grid>
        </Grid>
        <Grid container item style={{ padding: 8 }}>
          <Accordion
            sx={{ marginBottom: '8px', border: '1px solid', borderColor: 'divider' }}
          >
            <AccordionSummary expandIcon={<ArrowDropUp />}>
              <TitleBar title={'Boxes 1 - 13'} icon={AttachMoney} />
            </AccordionSummary>
            <AccordionDetails>
              <fieldset
                disabled={isFullyReconciled}
                style={{
                  border: 0,
                  margin: 0,
                  minWidth: 0,
                  padding: 0,
                  width: '100%',
                }}
              >
                <Grid container item style={{ padding: 8 }}>
                  <Grid container direction="row" spacing={2}>
                    <Grid xs={8} item>
                      <FormLabel>Insured I.D. Type</FormLabel>
                      <RadioGroup
                        row={true}
                        value={formik.values.q1Insurance}
                        onChange={this.setFormFieldValue(
                          'q1Insurance',
                          formik.setFieldValue
                        )}
                      >
                        <FormControlLabel
                          disabled={isFullyReconciled}
                          value={'MEDICARE'}
                          control={<Radio />}
                          label={
                            <Grid container direction="column">
                              <Grid item>MEDICARE</Grid>
                              <Grid item>(MEDICARE#)</Grid>
                            </Grid>
                          }
                        />
                        <FormControlLabel
                          disabled={isFullyReconciled}
                          value={'MEDICAID'}
                          control={<Radio />}
                          label={
                            <Grid container direction="column">
                              <Grid item>MEDICAID</Grid>
                              <Grid item>(MEDICAID#)</Grid>
                            </Grid>
                          }
                        />
                        <FormControlLabel
                          disabled={isFullyReconciled}
                          value={'TRICARE'}
                          control={<Radio />}
                          label={
                            <Grid container direction="column">
                              <Grid item>TRICARE</Grid>
                              <Grid item>(ID#/DoD#)</Grid>
                            </Grid>
                          }
                        />
                        <FormControlLabel
                          disabled={isFullyReconciled}
                          value={'CHAMPVA'}
                          control={<Radio />}
                          label={
                            <Grid container direction="column">
                              <Grid item>CHAMPVA</Grid>
                              <Grid item>(Member ID#)</Grid>
                            </Grid>
                          }
                        />
                        <FormControlLabel
                          disabled={isFullyReconciled}
                          value={'GROUP HEALTH PLAN'}
                          control={<Radio />}
                          label={
                            <Grid container direction="column">
                              <Grid item>GROUP HEALTH PLAN</Grid>
                              <Grid item>(ID#)</Grid>
                            </Grid>
                          }
                        />
                        <FormControlLabel
                          disabled={isFullyReconciled}
                          value={'FECA BLK LUNG'}
                          control={<Radio />}
                          label={
                            <Grid container direction="column">
                              <Grid item>FECA BLK LUNG</Grid>
                              <Grid item>(ID#)</Grid>
                            </Grid>
                          }
                        />
                        <FormControlLabel
                          disabled={isFullyReconciled}
                          value={'OTHER'}
                          control={<Radio />}
                          label={
                            <Grid container direction="column">
                              <Grid item>OTHER</Grid>
                              <Grid item>(ID#)</Grid>
                            </Grid>
                          }
                        />
                      </RadioGroup>
                    </Grid>
                    <Grid item xs={4}>
                      <FullWidthField
                        disabled={isFullyReconciled}
                        variant="standard"
                        name="q1AInsuranceId"
                        label="Insured's I.D. Number"
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    </Grid>
                  </Grid>
                  <Grid container direction="row" spacing={2}>
                    <Grid item xs={4}>
                      <FullWidthField
                        disabled={isFullyReconciled}
                        variant="standard"
                        name="q2PatientName"
                        label="Patient's Name (Last Name, First Name, Middle Initial)"
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    </Grid>
                    <Grid item xs={4} container direction="row">
                      <Grid item xs={8}>
                        <DatePickerField
                          disabled={isFullyReconciled}
                          variant="standard"
                          inputId="q3Date"
                          name="q3Date"
                          label="Patient's Birth Date"
                          shrink={true}
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <FormLabel>Sex</FormLabel>
                        <RadioGroup
                          row={true}
                          value={formik.values.q3Sex}
                          onChange={this.setFormFieldValue('q3Sex', formik.setFieldValue)}
                        >
                          <FormControlLabel
                            disabled={isFullyReconciled}
                            value={'Male'}
                            control={<Radio />}
                            label={'M'}
                          />
                          <FormControlLabel
                            disabled={isFullyReconciled}
                            value={'Female'}
                            control={<Radio />}
                            label={'F'}
                          />
                        </RadioGroup>
                      </Grid>
                    </Grid>
                    <Grid item xs={4}>
                      <FullWidthField
                        disabled={isFullyReconciled}
                        variant="standard"
                        name="q4InsuredName"
                        label="Insured's Name (Last Name, First Name, Middle Initial)"
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    </Grid>
                  </Grid>
                  <Grid container direction="row" spacing={2}>
                    <Grid item xs={4}>
                      <AddressField
                        disabled={isFullyReconciled}
                        name="q5PatientAddress"
                        label="Patient's Address (No., Street)"
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <FormLabel>Patient Relationship to Insured</FormLabel>
                      <RadioGroup
                        row={true}
                        value={formik.values.q6RelationshipToInsured}
                        onChange={this.setFormFieldValue(
                          'q6RelationshipToInsured',
                          formik.setFieldValue
                        )}
                      >
                        <FormControlLabel
                          disabled={isFullyReconciled}
                          value={'SELF'}
                          control={<Radio />}
                          label={'Self'}
                        />
                        <FormControlLabel
                          disabled={isFullyReconciled}
                          value={'SPOUSE'}
                          control={<Radio />}
                          label={'Spouse'}
                        />
                        <FormControlLabel
                          disabled={isFullyReconciled}
                          value={'CHILD'}
                          control={<Radio />}
                          label={'Child'}
                        />
                        <FormControlLabel
                          disabled={isFullyReconciled}
                          value={'OTHER'}
                          control={<Radio />}
                          label={'Other'}
                        />
                      </RadioGroup>
                      <FullWidthField
                        disabled={isFullyReconciled}
                        name="q8ReservedNucc"
                        label="Reserved For NUCC Use"
                        rows="3"
                        multiline={true}
                        variant="outlined"
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <AddressField
                        disabled={isFullyReconciled}
                        name="q7InsuredAddress"
                        label="Insured's Address (No., Street)"
                      />
                    </Grid>
                  </Grid>
                  <Grid container direction="row" spacing={2}>
                    <Grid
                      item
                      xs={4}
                      container
                      direction="column"
                      justifyContent="space-between"
                    >
                      <Grid item>
                        <FullWidthField
                          disabled={isFullyReconciled}
                          variant="standard"
                          name="q9OtherInsuredName"
                          label="Other Insured's Name (Last Name, First Name, Middle Initial)"
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item>
                        <FullWidthField
                          disabled={isFullyReconciled}
                          variant="standard"
                          name="q9AOtherInsuredPolicyNumber"
                          label="Other Insured's Policy or Group Number"
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item>
                        <FullWidthField
                          disabled={isFullyReconciled}
                          variant="standard"
                          name="q9BReservedNucc"
                          label="Reserved For NUCC Use"
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item>
                        <FullWidthField
                          disabled={isFullyReconciled}
                          variant="standard"
                          name="q9CReservedNucc"
                          label="Reserved For NUCC Use"
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item>
                        <FullWidthField
                          disabled={isFullyReconciled}
                          variant="standard"
                          name="q9DInsuranceProgramName"
                          label="Insurance Plan Name or Program Name"
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      item
                      xs={4}
                      container
                      direction="column"
                      justifyContent="space-between"
                    >
                      <Grid item>
                        <FormLabel>Is Patient's Condition Related To:</FormLabel>
                      </Grid>
                      <Grid item container direction="column">
                        <Grid item>
                          <FormLabel>Employment? (Current or Previous)</FormLabel>
                        </Grid>
                        <Grid item container direction="row" justifyContent="center">
                          <Grid item xs={8}>
                            <RadioGroup
                              row={true}
                              value={formik.values.q10ARelatedToEmployment}
                              onChange={this.setFormFieldValue(
                                'q10ARelatedToEmployment',
                                formik.setFieldValue
                              )}
                            >
                              <FormControlLabel
                                disabled={isFullyReconciled}
                                className="radioOption"
                                value={'true'}
                                control={<Radio />}
                                label="Yes"
                              />
                              <FormControlLabel
                                disabled={isFullyReconciled}
                                className="radioOption"
                                value={'false'}
                                control={<Radio />}
                                label="No"
                              />
                            </RadioGroup>
                          </Grid>
                        </Grid>
                        <Grid item>
                          <FormLabel>Auto Accident?</FormLabel>
                        </Grid>
                        <Grid item container direction="row" justifyContent="center">
                          <Grid item xs={4}>
                            <RadioGroup
                              row={true}
                              value={formik.values.q10BRelatedToAutoAccident}
                              onChange={this.setFormFieldValue(
                                'q10BRelatedToAutoAccident',
                                formik.setFieldValue
                              )}
                            >
                              <FormControlLabel
                                disabled={isFullyReconciled}
                                className="radioOption"
                                value={'true'}
                                control={<Radio />}
                                label="Yes"
                              />
                              <FormControlLabel
                                disabled={isFullyReconciled}
                                className="radioOption"
                                value={'false'}
                                control={<Radio />}
                                label="No"
                              />
                            </RadioGroup>
                          </Grid>
                          <Grid item xs={4}>
                            <TypeAheadField
                              label="Place (State)"
                              name="q10BAutoAccidentState"
                              cypressLabel="states"
                              childCypressLabel="state"
                              disabled={
                                isFullyReconciled ||
                                !formik.values.q10BRelatedToAutoAccident
                              }
                              fullWidth
                              setFieldValue={formik.setFieldValue}
                              selectedValue={formik.values.q10BAutoAccidentState}
                              items={States}
                              getName={getStateName}
                              getValue={getStateName}
                            />
                          </Grid>
                        </Grid>
                        <Grid item>
                          <FormLabel>Other Accident?</FormLabel>
                        </Grid>
                        <Grid item container direction="row" justifyContent="center">
                          <Grid item xs={8}>
                            <RadioGroup
                              row={true}
                              value={formik.values.q10CRelatedToOtherAccident}
                              onChange={this.setFormFieldValue(
                                'q10CRelatedToOtherAccident',
                                formik.setFieldValue
                              )}
                            >
                              <FormControlLabel
                                disabled={isFullyReconciled}
                                className="radioOption"
                                value={'true'}
                                control={<Radio />}
                                label="Yes"
                              />
                              <FormControlLabel
                                disabled={isFullyReconciled}
                                className="radioOption"
                                value={'false'}
                                control={<Radio />}
                                label="No"
                              />
                            </RadioGroup>
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item>
                        <FullWidthField
                          disabled={isFullyReconciled}
                          variant="standard"
                          name="q10DClaimCodes"
                          label="Claim Codes (Designated by NUCC)"
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    </Grid>
                    <Grid item xs={4} container direction="column">
                      <Grid item>
                        <FullWidthField
                          disabled={isFullyReconciled}
                          variant="standard"
                          name="q11PolicyFecaNumber"
                          label="Insured's Policy Group or FECA Number"
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item container direction="row">
                        <Grid item xs={8}>
                          <DatePickerField
                            disabled={isFullyReconciled}
                            variant="standard"
                            inputId="q11ADateOfBirth"
                            name="q11ADateOfBirth"
                            label="Insured's Date of Birth"
                            shrink={true}
                          />
                        </Grid>
                        <Grid item xs={4}>
                          <FormLabel>Sex</FormLabel>
                          <RadioGroup
                            row={true}
                            value={formik.values.q11ASex}
                            onChange={this.setFormFieldValue(
                              'q11ASex',
                              formik.setFieldValue
                            )}
                          >
                            <FormControlLabel
                              disabled={isFullyReconciled}
                              className="radioOption"
                              value={'Male'}
                              control={<Radio />}
                              label={'M'}
                            />
                            <FormControlLabel
                              disabled={isFullyReconciled}
                              className="radioOption"
                              value={'Female'}
                              control={<Radio />}
                              label={'F'}
                            />
                          </RadioGroup>
                        </Grid>
                      </Grid>
                      <Grid item>
                        <FullWidthField
                          disabled={isFullyReconciled}
                          variant="standard"
                          name="q11BOtherClaimId"
                          label="Other Claim Id (Designated by NUCC)"
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item>
                        <FullWidthField
                          disabled={isFullyReconciled}
                          variant="standard"
                          name="q11CInsuranceProgramName"
                          label="Insurance Plan Name or Program Name"
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item>
                        <FormLabel>Is there another health benefit plan?</FormLabel>
                      </Grid>
                      <Grid item container direction="row">
                        <Grid item xs={4}>
                          <RadioGroup
                            row={true}
                            value={formik.values.q11DAnotherBenefitPlan}
                            onChange={this.setFormFieldValue(
                              'q11DAnotherBenefitPlan',
                              formik.setFieldValue
                            )}
                          >
                            <FormControlLabel
                              disabled={isFullyReconciled}
                              className="radioOption"
                              value={'true'}
                              control={<Radio />}
                              label="Yes"
                            />
                            <FormControlLabel
                              disabled={isFullyReconciled}
                              className="radioOption"
                              value={'false'}
                              control={<Radio />}
                              label="No"
                            />
                          </RadioGroup>
                        </Grid>
                        <Grid item className="radioExtraText" xs={8}>
                          <strong className="radioExtraTextBold">If yes,</strong>
                          &nbsp; complete Other Insured's Name, Policy Number, and Plan
                          Name.
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid
                    container
                    direction="row"
                    justifyContent="space-between"
                    style={{ marginTop: '10px' }}
                  >
                    <Grid item container spacing={1} xs={8}>
                      <Grid item xs={12}>
                        READ BACK OF FORM BEFORE COMPLETING & SIGNING THIS FORM.
                      </Grid>
                      <Grid item xs={12}>
                        PATIENT'S OR AUTHORIZED PERSON'S SIGNATURE. I authorize the
                        release of any medical or other information necessary to process
                        this claim. I also request payment of government benefits either
                        to myself or to the party who accepts assignment below.
                      </Grid>
                      <Grid item xs={8}>
                        <FullWidthField
                          disabled={isFullyReconciled}
                          variant="standard"
                          name="q12PatientSignature"
                          label="Signed"
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <DatePickerField
                          disabled={isFullyReconciled}
                          variant="standard"
                          inputId="q12PatientSignatureDate"
                          name="q12PatientSignatureDate"
                          label="Date"
                        />
                      </Grid>
                    </Grid>
                    <Grid item container spacing={1} xs={4}>
                      <Grid item xs={12}>
                        INSURED'S OR AUTHORIZED PERSON'S SIGNATURE.
                      </Grid>
                      <Grid item xs={12}>
                        I authorize payment of medical benefits to the undersigned
                        physician or supplier for services described below.
                      </Grid>
                      <Grid item xs={12}>
                        <FullWidthField
                          disabled={isFullyReconciled}
                          variant="standard"
                          name="q13InsuredSignature"
                          label="Signed"
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </fieldset>
            </AccordionDetails>
          </Accordion>
        </Grid>

        <Grid container item style={{ padding: 8 }}>
          <Accordion
            defaultExpanded
            sx={{ marginBottom: '8px', border: '1px solid', borderColor: 'divider' }}
          >
            <AccordionSummary expandIcon={<ArrowDropUp />}>
              <TitleBar title={'Boxes 14 - 23'} icon={AttachMoney} />
            </AccordionSummary>
            <AccordionDetails>
              <fieldset
                disabled={isFullyReconciled}
                style={{
                  border: 0,
                  margin: 0,
                  minWidth: 0,
                  padding: 0,
                  width: '100%',
                }}
              >
                <Grid container item style={{ padding: 8 }}>
                  <Grid
                    container
                    direction="row"
                    justifyContent="space-between"
                    spacing={2}
                  >
                    <Grid item container xs={4} spacing={2}>
                      <Grid item xs={9}>
                        <DatePickerField
                          disabled={isFullyReconciled}
                          fullWidth
                          variant="standard"
                          inputId="q14DateOfIllness"
                          name="q14DateOfIllness"
                          label="Date of Current Illness, Injury, or Pregnancy (LMP)"
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <FullWidthField
                          disabled={isFullyReconciled}
                          variant="standard"
                          name="q14Qualifier"
                          label="Qualifier"
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    </Grid>
                    <Grid item container xs={4} spacing={2}>
                      <Grid item xs={3}>
                        <FullWidthField
                          disabled={isFullyReconciled}
                          variant="standard"
                          name="q15Qualifier"
                          label="Qualifier"
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={9}>
                        <DatePickerField
                          disabled={isFullyReconciled}
                          fullWidth
                          variant="standard"
                          inputId="q15OtherDate"
                          name="q15OtherDate"
                          label="Other Date"
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      item
                      container
                      xs={4}
                      justifyContent="flex-start"
                      spacing={2}
                      style={{ paddingRight: '8px' }}
                    >
                      <Grid item xs={12}>
                        <FormLabel>
                          Dates patient unable to work in current occupation
                        </FormLabel>
                      </Grid>
                      <Grid item container xs={12} spacing={4}>
                        <Grid item xs={6}>
                          <DatePickerField
                            disabled={isFullyReconciled}
                            variant="standard"
                            inputId="q16UnableToWorkFrom"
                            name="q16UnableToWorkFrom"
                            label="From"
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <DatePickerField
                            disabled={isFullyReconciled}
                            variant="standard"
                            inputId="q16UnabletoWorkTo"
                            name="q16UnabletoWorkTo"
                            label="To"
                            fullWidth
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid
                    container
                    direction="row"
                    justifyContent="space-between"
                    spacing={2}
                  >
                    <Grid item container xs={4} justifyContent="flex-start" spacing={2}>
                      <Grid item xs={3}>
                        <FullWidthField
                          disabled={isFullyReconciled}
                          variant="standard"
                          name="q17Qualifier"
                          label="Qualifier"
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={9}>
                        <FullWidthField
                          disabled={isFullyReconciled}
                          variant="standard"
                          name="q17NameOfReferring"
                          label="Name of Referring Provider or Other Source"
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    </Grid>

                    <Grid item container xs={4} justifyContent="flex-start" spacing={2}>
                      <Grid item xs={3}>
                        <FullWidthField
                          disabled={isFullyReconciled}
                          variant="standard"
                          name="q17AQualifier"
                          label="Qualifier"
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={9}>
                        <FullWidthField
                          disabled={isFullyReconciled}
                          variant="standard"
                          name="q17AId"
                          label="ID"
                          InputLabelProps={{ shrink: true }}
                        />
                        <FullWidthField
                          disabled={isFullyReconciled}
                          variant="standard"
                          name="q17BNpi"
                          label="NPI"
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    </Grid>

                    <Grid
                      item
                      container
                      xs={4}
                      justifyContent="flex-start"
                      spacing={2}
                      style={{ paddingRight: '8px' }}
                    >
                      <Grid item xs={12}>
                        <FormLabel>
                          Hospitalization Dates Related to Current Services
                        </FormLabel>
                      </Grid>
                      <Grid item container xs={12} spacing={4}>
                        <Grid item xs={6}>
                          <DatePickerField
                            disabled={isFullyReconciled}
                            variant="standard"
                            inputId="q18HospitalizationFrom"
                            name="q18HospitalizationFrom"
                            label="From"
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <DatePickerField
                            disabled={isFullyReconciled}
                            variant="standard"
                            inputId="q18HospitalizationTo"
                            name="q18HospitalizationTo"
                            label="To"
                            fullWidth
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid
                    container
                    direction="row"
                    justifyContent="space-between"
                    spacing={2}
                  >
                    <Grid item xs={8}>
                      <FullWidthField
                        disabled={isFullyReconciled}
                        variant="standard"
                        name="q19AdditionalClaims"
                        label="Additional Claim Information (Designated by NUCC)"
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    </Grid>
                    <Grid item container xs={4}>
                      <Grid item xs={6}>
                        <FormLabel>Outside Lab?</FormLabel>
                        <RadioGroup
                          row={true}
                          value={formik.values.q20OutsideLab}
                          onChange={this.setFormFieldValue(
                            'q20OutsideLab',
                            formik.setFieldValue
                          )}
                        >
                          <FormControlLabel
                            disabled={isFullyReconciled}
                            value={'true'}
                            control={<Radio />}
                            label={'Yes'}
                          />
                          <FormControlLabel
                            disabled={isFullyReconciled}
                            value={'false'}
                            control={<Radio />}
                            label={'No'}
                          />
                        </RadioGroup>
                      </Grid>
                      <Grid item xs={6}>
                        <FullWidthField
                          disabled={isFullyReconciled}
                          variant="standard"
                          name="q20Charges"
                          label="Charges"
                          type="number"
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid
                    container
                    direction="row"
                    justifyContent="space-between"
                    spacing={2}
                  >
                    <Grid item container xs={8} justifyContent="space-between">
                      <Grid item xs={9} style={{ position: 'relative' }}>
                        <p
                          style={{
                            bottom: 0,
                            margin: 0,
                            position: 'absolute',
                          }}
                        >
                          Diagnosis or Nature of Illness or Injury (Relate A-L to service
                          line below) *
                        </p>
                      </Grid>
                      <Grid item xs={3}>
                        <FullWidthField
                          disabled={isFullyReconciled}
                          variant="standard"
                          name="q21DiagnosisIcdInd"
                          label="ICD Ind."
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item container direction="row" xs={12}>
                        <FieldArray
                          name="q21Diagnosis"
                          render={this.renderDiagnosisFields(
                            formik.values,
                            isFullyReconciled
                          )}
                        />
                        <ErrorMessage name="q21Diagnosis">
                          {(msg) => <div style={{ color: 'red' }}>{msg}</div>}
                        </ErrorMessage>
                      </Grid>
                    </Grid>
                    <Grid item container xs={4} justifyContent="space-between">
                      <Grid item xs={6}>
                        <FullWidthField
                          disabled={isFullyReconciled}
                          variant="standard"
                          name="q22ResubmissionCode"
                          label="Resubmission Code"
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <FullWidthField
                          disabled={isFullyReconciled}
                          variant="standard"
                          name="q22OriginalRefNo"
                          label="Original Ref. No."
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FullWidthField
                          disabled={isFullyReconciled}
                          variant="standard"
                          name="q23PrioAuthorizationNumber"
                          label="Prior Authorization Number"
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </fieldset>
            </AccordionDetails>
          </Accordion>
        </Grid>

        <Grid container item style={{ padding: 8 }}>
          <Accordion
            defaultExpanded
            sx={{ marginBottom: '8px', border: '1px solid', borderColor: 'divider' }}
          >
            <AccordionSummary expandIcon={<ArrowDropUp />}>
              <TitleBar title={'Box 24'} icon={AttachMoney} />
            </AccordionSummary>
            <AccordionDetails>
              {this.renderBox24(formik.values, disableDollarAmounts, isFullyReconciled)}
            </AccordionDetails>
          </Accordion>
        </Grid>
        <Grid container item style={{ padding: 8 }}>
          <Accordion sx={{ border: '1px solid', borderColor: 'divider' }}>
            <AccordionSummary expandIcon={<ArrowDropUp />}>
              <TitleBar title={'Boxes 25 - 33'} icon={AttachMoney} />
            </AccordionSummary>
            <AccordionDetails>
              <fieldset
                disabled={isFullyReconciled}
                style={{
                  border: 0,
                  margin: 0,
                  minWidth: 0,
                  padding: 0,
                  width: '100%',
                }}
              >
                <Grid container item style={{ padding: 8 }}>
                  <Grid
                    container
                    direction="row"
                    justifyContent="space-between"
                    spacing={2}
                  >
                    <Grid item container xs={4} spacing={2}>
                      <Grid item xs={7}>
                        <FullWidthField
                          disabled={isFullyReconciled}
                          variant="standard"
                          name="q25FederalTaxId"
                          label="Federal Tax I.D. Number"
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={5}>
                        <RadioGroup
                          row={true}
                          value={formik.values.q25FederalTaxIdSource}
                          onChange={this.setFormFieldValue(
                            'q25FederalTaxIdSource',
                            formik.setFieldValue
                          )}
                        >
                          <FormControlLabel
                            disabled={isFullyReconciled}
                            value={'SSN'}
                            control={<Radio />}
                            label={'SSN'}
                          />
                          <FormControlLabel
                            disabled={isFullyReconciled}
                            value={'EIN'}
                            control={<Radio />}
                            label={'EIN'}
                          />
                        </RadioGroup>
                      </Grid>
                    </Grid>
                    <Grid item container xs={4} spacing={2}>
                      <Grid item xs={6}>
                        <FullWidthField
                          disabled={isFullyReconciled}
                          variant="standard"
                          name="q26PatientAccountNo"
                          label="Patient's Account No."
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <FormLabel>
                          Accept Assignment? (For govt. claims, see back)
                        </FormLabel>
                        <RadioGroup
                          row={true}
                          value={formik.values.q27AcceptAssignment}
                          onChange={this.setFormFieldValue(
                            'q27AcceptAssignment',
                            formik.setFieldValue
                          )}
                        >
                          <FormControlLabel
                            disabled={isFullyReconciled}
                            value={'true'}
                            control={<Radio />}
                            label={'Yes'}
                          />
                          <FormControlLabel
                            disabled={isFullyReconciled}
                            value={'false'}
                            control={<Radio />}
                            label={'No'}
                          />
                        </RadioGroup>
                      </Grid>
                    </Grid>
                    <Grid item container xs={4} justifyContent="space-between">
                      <Grid item xs={3}>
                        <FullWidthField
                          variant="standard"
                          name="q28TotalCharge"
                          label="Total Charge"
                          type="number"
                          className="bottomItem"
                          disabled={disableDollarAmounts}
                          InputLabelProps={{
                            shrink: true,
                          }}
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <FullWidthField
                          variant="standard"
                          name="q29AmountPaid"
                          label="Amount Paid"
                          type="number"
                          className="bottomItem"
                          disabled
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <FullWidthField
                          disabled={isFullyReconciled}
                          variant="standard"
                          name="q30ReservedNucc"
                          label="Reserved for NUCC Use"
                          type="number"
                          className="bottomItem"
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid
                    container
                    direction="row"
                    justifyContent="space-between"
                    spacing={2}
                    alignItems="flex-end"
                  >
                    <Grid
                      item
                      container
                      xs={4}
                      justifyContent="space-between"
                      style={{ display: 'flex', flexDirection: 'column' }}
                    >
                      <Grid item xs={12}>
                        <FormLabel>
                          Signature of Physician or Supplier Including Degrees or
                          Credentials
                        </FormLabel>
                        <FormLabel style={{ display: 'block', marginTop: '30px' }}>
                          (I certify that the statements on the reverse apply to this bill
                          and are made a part thereof.)
                        </FormLabel>
                      </Grid>
                      <Grid
                        item
                        container
                        spacing={2}
                        xs={12}
                        style={{ marginTop: 'auto' }}
                      >
                        <Grid item xs={7}>
                          <FullWidthField
                            disabled={isFullyReconciled}
                            name="q31Signature"
                            variant="standard"
                            label="Signature"
                            className="bottomItem"
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={5}>
                          <DatePickerField
                            disabled={isFullyReconciled}
                            variant="standard"
                            inputId="q31Date"
                            name="q31Date"
                            label="Date"
                            fullWidth
                            className="bottomItem"
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid
                      item
                      container
                      xs={4}
                      justifyContent="space-between"
                      alignContent="flex-end"
                    >
                      <Grid item xs={12}>
                        <FullWidthField
                          name="q32LocationInfo"
                          label="Service Facility Location Information"
                          rows="3"
                          multiline={true}
                          variant="outlined"
                          disabled
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <FullWidthField
                          variant="standard"
                          name="q32ALocationNpi"
                          label="Service Facility NPI"
                          disabled
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={5}>
                        <FullWidthField
                          disabled={isFullyReconciled}
                          variant="standard"
                          name="q32B"
                          label=" " // this is to fix the spacing
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      item
                      container
                      xs={4}
                      justifyContent="space-between"
                      alignContent="flex-end"
                    >
                      <Grid item xs={5} style={{ paddingTop: '24px' }}>
                        <FormLabel>Billing Provider</FormLabel>
                      </Grid>
                      <Grid item xs={4} style={{ paddingBottom: '8px' }}>
                        <FullWidthField
                          disabled={isFullyReconciled}
                          variant="standard"
                          name="q33BillingProviderPhoneNumber"
                          label="Phone Number"
                          InputLabelProps={{ shrink: true }}
                          InputProps={{ inputComponent: PhoneNumberMask }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FullWidthField
                          name="q33BillingProviderInfo"
                          label="Information"
                          rows="2"
                          disabled
                          multiline={true}
                          variant="outlined"
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <FullWidthField
                          name="q33ABillingProviderNpi"
                          label="Billing Provider NPI"
                          disabled
                          InputLabelProps={{ shrink: true }}
                          variant="standard"
                        />
                      </Grid>
                      <Grid item xs={5}>
                        <FullWidthField
                          disabled={isFullyReconciled}
                          name="q33B"
                          label=" " // this is to fix the spacing
                          InputLabelProps={{ shrink: true }}
                          variant="standard"
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </fieldset>
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid>
    )
  }

  private renderDiagnosisFields = (values: IClaimFormValues, disabled: boolean) => () => (
    <>
      {values.q21Diagnosis.map((diagnosis: Diagnosis, index: number) => (
        <Grid item xs={3} key={index}>
          <FullWidthField
            disabled={disabled}
            variant="standard"
            name={`q21Diagnosis.${index}.code`}
            label={diagnosis.pointer}
          />
        </Grid>
      ))}
    </>
  )

  private openContractModal(index: number) {
    this.props.openContractModal!(index)
  }

  private renderBox24 = (
    values: IClaimFormValues,
    disableDollarAmounts: boolean,
    disabled: boolean
  ) => {
    return (
      <>
        <Grid container direction="column" spacing={1}>
          {values.q24.map((_lineItem: ILineItemFormValues, index: number) => (
            <Box24
              key={index}
              disabled={disabled}
              showContractAmount={this.props.showContractModalButton}
              disableDollarAmountEditing={disableDollarAmounts}
              disableLineItemActions={disableDollarAmounts}
              openContractModal={() => this.openContractModal(index)}
              index={index}
              providerTypeId={values.providerTypeId}
              providerId={values.providerId}
            />
          ))}
        </Grid>
      </>
    )
  }

  private setFormFieldValue =
    (
      field: string,
      setFieldValue: (field: string, value: string, shouldValidate?: boolean) => void
    ) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFieldValue(field, event.target.value, true)
    }

  private getProviderTypes = (values: IClaimFormValues) => {
    return values.providerLocation ? values.providerLocation.providerTypes || [] : []
  }

  private getProviderTypeName = (type: IProviderTypeDto) => {
    return type.name || ''
  }

  private getProviderTypeValue = (type: IProviderTypeDto) => {
    return type.id || ''
  }

  private getLocationProvider = (values: IClaimFormValues) => {
    if (this.props.selectedLocation) {
      values.providerLocationId = this.props.selectedLocation.id
      values.providerLocation = this.props.selectedLocation
      values.q32ALocationNpi = this.props.selectedLocation.npi ?? ''
      values.q33ABillingProviderNpi =
        this.props.selectedLocation.npi! !== ''
          ? this.props.selectedLocation.npi!
          : this.props.selectedLocation.provider?.npiNumber! !== ''
            ? this.props.selectedLocation.provider?.npiNumber!
            : ''

      values.q32LocationInfo =
        (this.props.selectedLocation?.name
          ? this.props.selectedLocation?.name + '\n'
          : '') +
        (this.props.selectedLocation?.physicalAddress?.line1
          ? this.props.selectedLocation.physicalAddress.line1 + '\n'
          : '') +
        (this.props.selectedLocation?.physicalAddress?.line2
          ? this.props.selectedLocation.physicalAddress.line2 + '\n'
          : '') +
        (this.props.selectedLocation?.physicalAddress?.city
          ? this.props.selectedLocation.physicalAddress.city
          : '') +
        (this.props.selectedLocation?.physicalAddress?.state
          ? ', ' + this.props.selectedLocation?.physicalAddress?.state
          : '') +
        (this.props.selectedLocation?.physicalAddress?.zip
          ? ' ' + this.props.selectedLocation?.physicalAddress?.zip
          : '')

      if (
        this.props.selectedLocation &&
        this.props.selectedLocation!.billingAddresses !== undefined
      ) {
        var billingAddress = new AddressDto()
        for (var i = 0; i < this.props.selectedLocation!.billingAddresses.length; i++) {
          var address = this.props.selectedLocation!.billingAddresses[i]
          if (address.isActive && address.address != undefined) {
            billingAddress = address.address
            break
          }
        }

        values.q33BillingProviderInfo =
          (billingAddress?.name ? billingAddress?.name + '\n' : '') +
          (billingAddress?.line1 ? billingAddress.line1 + '\n' : '') +
          (billingAddress?.line2 ? billingAddress.line2 + '\n' : '') +
          (billingAddress?.city ? billingAddress.city : '') +
          (billingAddress?.state ? ', ' + billingAddress?.state : '') +
          (billingAddress?.zip ? ' ' + billingAddress?.zip : '')
      }

      values.provider = this.props.selectedLocation!.provider!
      values.providerId = this.props.selectedLocation.providerId!

      if (this.props.selectedLocation!.provider) {
        values.provider = this.props.selectedLocation!.provider!
        values.providerId = this.props.selectedLocation.providerId!
        return this.props.selectedLocation?.provider?.name ?? '{UNKNOWN PROVIDER GROUP}'
      }
      return ''
    }
    return values.providerLocation?.provider?.name ?? '{UNKNOWN PROVIDER GROUP}'
  }

  private isProviderTypeDisabled = (values: IClaimFormValues) => {
    if (!!values.provider) {
      return false
    }
    return true
  }

  private handleDueDateChange = (event: any) => {
    this.props.handleDueDateChange!(event.target.value)
  }
}

const InjectedFullForm = inject<IStores, IFullFormProps, Partial<IFullFormProps>, any>(
  (stores: IStores) => ({
    loadClaim: stores.cms1500.loadCMS1500,
    selectedCMS1500: stores.cms1500.selectedCMS1500,
    selectedSchedule: stores.schedules.selectedSchedule,
    setSelectedCMS1500: stores.cms1500.setSelectedCMS1500,
    openContractModal: stores.cms1500.openContractModal,
    openReferralContactModal: stores.patientEdit.openReferralContactModal,
    isReferralContactModalOpen: stores.patientEdit.isReferralContactModalOpen,
  })
)(FullForm)

export default connect<IFullFormProps, IClaimFormValues>(InjectedFullForm)
